import jsPDF from 'jspdf';
import 'jspdf-autotable';

declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}

export function generateWorkflowOnePager(institutionName: string) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Header
  doc.setFontSize(24);
  doc.setTextColor(52, 62, 79); // primary color approximation
  doc.text('AthleteNexus', pageWidth / 2, 20, { align: 'center' });
  
  doc.setFontSize(12);
  doc.setTextColor(102, 102, 102);
  doc.text('NIL Compliance Workflow Overview', pageWidth / 2, 28, { align: 'center' });
  
  // Institution name
  doc.setFontSize(10);
  doc.setTextColor(153, 153, 153);
  doc.text(`For: ${institutionName}`, pageWidth / 2, 35, { align: 'center' });
  
  // Workflow Diagram Section
  doc.setFontSize(14);
  doc.setTextColor(52, 62, 79);
  doc.text('Complete Workflow Process', 15, 50);
  
  const workflowSteps = [
    { step: '1. Deal Intake', time: '< 5 min', desc: 'Athletes submit NIL deals via portal' },
    { step: '2. AI Analysis', time: '< 30 sec', desc: 'Automated compliance assessment using AI' },
    { step: '3. Conflict Detection', time: '< 10 sec', desc: 'Check institutional sponsors & prohibited categories' },
    { step: '4. Approval Review', time: '2.3 hrs avg', desc: 'Compliance officers review with AI insights' },
    { step: '5. Archive & Audit', time: 'Automatic', desc: 'Hash-chain verification for immutable records' },
    { step: '6. Export Reports', time: '< 1 min', desc: 'NCAA-ready compliance documentation' }
  ];
  
  doc.autoTable({
    startY: 55,
    head: [['Step', 'Duration', 'Description']],
    body: workflowSteps.map(s => [s.step, s.time, s.desc]),
    theme: 'striped',
    headStyles: { fillColor: [52, 62, 79] },
    margin: { left: 15, right: 15 }
  });
  
  // SLA Matrix
  const slaY = (doc as any).lastAutoTable.finalY + 15;
  doc.setFontSize(14);
  doc.setTextColor(52, 62, 79);
  doc.text('Service Level Agreements', 15, slaY);
  
  doc.autoTable({
    startY: slaY + 5,
    head: [['Deal Type', 'Review Time', 'SLA Target']],
    body: [
      ['Standard (< $5k)', '48 hours', '95%'],
      ['High-Value ($5k-$25k)', '72 hours', '98%'],
      ['Premium (> $25k)', '96 hours', '99%'],
      ['Critical Risk', '4 hours', '100%']
    ],
    theme: 'grid',
    headStyles: { fillColor: [52, 62, 79] },
    margin: { left: 15, right: 15 }
  });
  
  // Retention Policy
  const retentionY = (doc as any).lastAutoTable.finalY + 15;
  doc.setFontSize(14);
  doc.setTextColor(52, 62, 79);
  doc.text('Data Retention Policy', 15, retentionY);
  
  doc.autoTable({
    startY: retentionY + 5,
    head: [['Data Type', 'Retention Period']],
    body: [
      ['Active Deal Records', 'Indefinite'],
      ['Audit Logs', '7 years (SOC 2 / NCAA)'],
      ['AI Analysis History', '3 years'],
      ['Archived Deals', 'Permanent'],
      ['Financial Records', '7 years (IRS)']
    ],
    theme: 'grid',
    headStyles: { fillColor: [52, 62, 79] },
    margin: { left: 15, right: 15 }
  });
  
  // Role Permissions
  const roleY = (doc as any).lastAutoTable.finalY + 15;
  
  // Check if we need a new page
  if (roleY > 240) {
    doc.addPage();
    doc.setFontSize(14);
    doc.setTextColor(52, 62, 79);
    doc.text('Role-Based Permissions', 15, 20);
    
    doc.autoTable({
      startY: 25,
      head: [['Role', 'Submission', 'Approval', 'Analytics', 'Audit Access']],
      body: [
        ['Super Admin', 'Full', 'Full', 'Full', 'Full (7 years)'],
        ['Compliance Officer', 'Full', 'Full', 'Full', 'Full (7 years)'],
        ['Athletic Director', 'Full', 'Dept Only', 'Full', 'Dept (7 years)'],
        ['Coach', 'Full', 'None', 'Team Only', 'Limited (1 year)']
      ],
      theme: 'grid',
      headStyles: { fillColor: [52, 62, 79] },
      margin: { left: 15, right: 15 }
    });
  } else {
    doc.setFontSize(14);
    doc.setTextColor(52, 62, 79);
    doc.text('Role-Based Permissions', 15, roleY);
    
    doc.autoTable({
      startY: roleY + 5,
      head: [['Role', 'Submission', 'Approval', 'Analytics', 'Audit Access']],
      body: [
        ['Super Admin', 'Full', 'Full', 'Full', 'Full (7 years)'],
        ['Compliance Officer', 'Full', 'Full', 'Full', 'Full (7 years)'],
        ['Athletic Director', 'Full', 'Dept Only', 'Full', 'Dept (7 years)'],
        ['Coach', 'Full', 'None', 'Team Only', 'Limited (1 year)']
      ],
      theme: 'grid',
      headStyles: { fillColor: [52, 62, 79] },
      margin: { left: 15, right: 15 }
    });
  }
  
  // Footer with contact
  const finalY = doc.internal.pageSize.getHeight() - 20;
  doc.setFontSize(9);
  doc.setTextColor(102, 102, 102);
  doc.text('AthleteNexus • athletenexusnil.com • info@athletenexusnil.com', pageWidth / 2, finalY, { align: 'center' });
  doc.text('99.5% Uptime Guarantee • SOC 2 Type II Compliant • Enterprise-Grade Security', pageWidth / 2, finalY + 5, { align: 'center' });
  
  // Save the PDF
  doc.save('athletenexus-workflow-onepager.pdf');
}
